﻿import IMask from 'imask';
import { createPopper, Placement } from '@popperjs/core';

import { BlazorLocalStorage } from './components/blazor-local-storage';
import { BlazorSessionStorage } from './components/blazor-session-storage';
import { ConnectivityHandler } from './components/connectivity-handler';
import { InstallationHandler } from './components/installation-handler';
import { NotificationsHandler } from './components/notifications-handler';
import { OIDC } from './components/oidc';
import 'emoji-picker-element';

// Global window declarations
declare global {
    interface Window {
        BlazorLocalStorage: typeof BlazorLocalStorage;
        BlazorSessionStorage: typeof BlazorSessionStorage;
        NotificationsHandler: typeof NotificationsHandler;
        OIDC: typeof OIDC;
        ConnectivityHandler?: ConnectivityHandler;
        InstallationHandler?: InstallationHandler;
        initializeDotNetObject: (dotNetObject: DotNet.DotNetObject) => void;
        setPageTitle: (title: string) => void;
        getElementHeight: (element: Element) => number | null;
        getElementDistanceToTop: (element: Element) => number | null;
        getElementDistanceToBottom: (element: Element) => number | null;
        showImagePreview: (inputElement: HTMLInputElement, imgElement: HTMLImageElement) => void;
        tableObserverInit: (tableId: string) => void;
        maskInputs: () => void;
        textAreaAdjustRows: (element: Element) => void;
        textAreaAdjustRowsEvent: (element: Element) => void;
        saveAsFile: (filename: string, bytesBase64: string, type?: string) => void;
        getDataItemsDisplayType: (discriminator: string) => string;
        getWindowInnerWidth: () => number;
        setDataItemsDisplayType: (discriminator: string, displayType: string) => void;
        scrollToElement: (element: Element) => void;
        scrollToBottom: (element: Element) => void;
        scrollToElementByQuery: (querySelector: string) => void;
        adjustNavbarHeight: () => void;
        navbarCollapse: () => void;
        changeUrl: (string: string) => void;
        facebookSdkRescan: (delayMs: number) => void;
        insertTextAtCursorPosition: (textAreaElement: HTMLTextAreaElement, text: string, dotNetRef: DotNet.DotNetObject) => void;
        getSelectedEmoji: (event: any) => string;
        initializeEmojiPicker: (emojiPickerElement: Element, textAreaElement: HTMLTextAreaElement, dotNetRef: DotNet.DotNetObject) => void;
        closeEmojiPicker: (event: MouseEvent) => void;
        setClarityEnabled: (isEnabled: boolean) => void;
        startClarity: () => void;
        createBlazorPortalTooltip: (triggerElement: HTMLElement, tooltipElement: HTMLElement, placement: string) => TooltipInstance;
        createPortalMenu: (triggerElement: HTMLElement, menuElement: HTMLElement, hostElement: HTMLElement, placement: string, dotNetRef: DotNet.DotNetObject) => TooltipInstance;
        createSelectMenu: (triggerElement: HTMLElement, tooltipElement: HTMLElement) => TooltipInstance;
        emojiPickerElementDotNetRef?: DotNet.DotNetObject;
        registerDragDropComponent: (dragAndDropComponent: Element, componentRef: DotNet.DotNetObject) => void;
        unregisterDragDropComponent: (dragAndDropComponent: Element) => void;
    }
}

// TypeScript interfaces for better type safety
interface TooltipOptions {
    placement: Placement;
    text: string;
    tooltipClass?: string;
    tooltipStyle?: string;
    tooltipId: string;
}

interface TooltipInstance {
    show: () => void;
    hide: () => void;
    destroy: () => void;
}

interface Dictionary<T> {
    [key: string]: T;
}

// Initialize global objects
window.BlazorLocalStorage = BlazorLocalStorage;
window.BlazorSessionStorage = BlazorSessionStorage;
window.NotificationsHandler = NotificationsHandler;
window.OIDC = OIDC;

// Load SVG sprites
const xhr = new XMLHttpRequest();
xhr.open('GET', '../images/icons.svg');
xhr.onload = (): void => {
    const svgSpritesElement = document.getElementById('svgSprites');
    if (svgSpritesElement) {
        svgSpritesElement.innerHTML = xhr.responseText;
    }
};
xhr.send();

// Function called from WebAssembly
window.initializeDotNetObject = (dotNetObject: DotNet.DotNetObject): void => {
    window.ConnectivityHandler = new ConnectivityHandler(dotNetObject);
    window.InstallationHandler = new InstallationHandler(dotNetObject);
    console.log("DOM dotNetObject initialized.");
};

window.setPageTitle = (title: string): void => {
    document.title = title;
};

window.getElementHeight = (element: Element): number | null => {
    if (!element) return null;

    const htmlElement = element as HTMLElement;
    const offsetHeight: number = htmlElement.offsetHeight;

    if (!offsetHeight || offsetHeight > (window.innerHeight - element.getBoundingClientRect().top)) {
        return Math.floor(window.innerHeight - element.getBoundingClientRect().top);
    }

    return Math.floor(offsetHeight);
};

window.getElementDistanceToTop = (element: Element): number | null => {
    return element ? Math.floor(element.getBoundingClientRect().y) : null;
};

window.getElementDistanceToBottom = (element: Element): number | null => {
    return element ? Math.floor(window.innerHeight - element.getBoundingClientRect().y) : null;
};

window.showImagePreview = (inputElement: HTMLInputElement, imgElement: HTMLImageElement): void => {
    if (!inputElement.files?.[0]) return;

    const url = URL.createObjectURL(inputElement.files[0]);
    imgElement.addEventListener('load', () => URL.revokeObjectURL(url), { once: true });
    imgElement.src = url;
};

window.tableObserverInit = (tableId: string): void => {
    if (!tableId) return;

    const tableBodyElement = document.getElementById(tableId)?.querySelector("tbody");
    if (!tableBodyElement) return;

    const observer = new MutationObserver(window.maskInputs);
    observer.observe(tableBodyElement, { childList: true });
};

window.maskInputs = (): void => {
    const inputMaskParameterName = "data-inputmask";
    const elements = document.querySelectorAll<HTMLElement>(`[${inputMaskParameterName}]`);

    elements.forEach((element: HTMLElement) => {
        const maskValue = element.getAttribute(inputMaskParameterName);
        if (!maskValue) return;

        if (element instanceof HTMLInputElement) {
            if (maskValue.toLowerCase() === "number") {
                new IMask.InputMask(element, {
                    mask: Number,
                    thousandsSeparator: " ",
                    min: Number(element.getAttribute("min")) || 0,
                    max: Number(element.getAttribute("max")) || 999,
                    scale: Number(element.getAttribute("scale")) || 0,
                    padFractionalZeros: element.getAttribute("padFractionalZeros") === "true"
                });
            } else {
                new IMask.InputMask(element, { mask: maskValue });
            }
        } else {
            const masked = IMask.createMask({ mask: maskValue });
            element.innerHTML = masked.resolve(element.innerHTML);
        }
    });
};

const adjustTextAreaRows = (element: HTMLTextAreaElement): void => {
    const maxRows = 8;
    element.rows = 1;
    const computedStyle = window.getComputedStyle(element);
    const lineHeight = parseFloat(computedStyle.lineHeight);
    const paddingTop = parseFloat(computedStyle.paddingTop);
    const paddingBottom = parseFloat(computedStyle.paddingBottom);
    const adjustedHeight = element.scrollHeight - paddingTop - paddingBottom;
    const lines = Math.floor(adjustedHeight / lineHeight);
    element.rows = Math.min(lines, maxRows);
};

window.textAreaAdjustRows = (element: Element): void => {
    if (!(element instanceof HTMLTextAreaElement)) return;

    const resize = (): void => adjustTextAreaRows(element);

    element.removeEventListener('input', resize);
    element.addEventListener('input', resize);
    resize();
};

window.textAreaAdjustRowsEvent = (element: Element): void => {
    if (element instanceof HTMLTextAreaElement) {
        adjustTextAreaRows(element);
    }
};

window.saveAsFile = (filename: string, bytesBase64: string, type = "text/plain"): void => {
    const link = document.createElement('a');
    link.download = filename;
    link.href = URL.createObjectURL(new Blob([bytesBase64]));
    link.type = type;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

window.getDataItemsDisplayType = (discriminator: string): string => {
    const dictionary = JSON.parse(localStorage.getItem("dataItemsDisplayType") || "{}") as Dictionary<string>;
    return dictionary[discriminator] || (window.innerWidth >= 1536 ? "Table" : "Cards");
};

window.getWindowInnerWidth = (): number => window.innerWidth;

window.setDataItemsDisplayType = (discriminator: string, displayType: string): void => {
    const dictionary = JSON.parse(localStorage.getItem("dataItemsDisplayType") || "{}") as Dictionary<string>;
    dictionary[discriminator] = displayType;
    localStorage.setItem("dataItemsDisplayType", JSON.stringify(dictionary));
};

window.scrollToElement = (element: Element): void => {
    element?.scrollIntoView({ behavior: "smooth" });
};

window.scrollToBottom = (element: Element): void => {
    if (!(element instanceof HTMLElement)) {
        console.warn("Provided element is not an HTMLElement.");
        return;
    }

    requestAnimationFrame(() => {
        element.scrollTop = element.scrollHeight;
    });
};

window.scrollToElementByQuery = (querySelector: string): void => {
    if (!querySelector) return;

    const element = document.querySelector(querySelector);
    if (!element) {
        console.warn(`Element not found by query selector: ${querySelector}`);
        return;
    }
    element.scrollIntoView({ behavior: "smooth" });
};

window.adjustNavbarHeight = (): void => {
    const alertWrapper = document.querySelector<HTMLElement>('.alert-wrapper');
    const navbar = document.querySelector<HTMLElement>('.navbar');

    if (alertWrapper && navbar) {
        const topElementHeight = alertWrapper.offsetHeight + 1;
        navbar.style.height = window.innerWidth < 768 ? 'auto' : `calc(100dvh - ${topElementHeight}px)`;
    }
};

// Event listeners
window.addEventListener('resize', window.adjustNavbarHeight);

// Close Navbar when clicked outside
document.addEventListener('click', (event: MouseEvent): void => {
    const targetElement = event.target as HTMLElement;
    const navbarToggler = document.querySelector<HTMLElement>('.navbar .navbar-toggler');
    const isClickedWithinNavbar = targetElement.closest('.navbar') !== null;
    const isClickedWithinNavbarDropdown = targetElement.closest('.nav-item.dropdown') !== null;
    const isNavbarExpanded = navbarToggler?.getAttribute('aria-expanded') === 'true';

    if (isClickedWithinNavbar && !isClickedWithinNavbarDropdown && isNavbarExpanded) {
        navbarCollapse();
    }
});

const navbarCollapse = (): void => {
    const navbarToggler = document.querySelector<HTMLElement>('.navbar .navbar-toggler');
    if (navbarToggler?.getAttribute('aria-expanded') === 'true') {
        navbarToggler.click();
    }
};

window.navbarCollapse = navbarCollapse;

window.changeUrl = (url: string): void => {
    history.pushState(null, '', url);
};

window.facebookSdkRescan = (delayMs: number): void => {
    setTimeout(() => {
        (window as any).FB?.XFBML?.parse();
    }, delayMs);
};

const getCookieValue = (cookieName: string): string | null => {
    const cookies = document.cookie.split(';');
    for (const cookie of cookies) {
        const [name, value] = cookie.trim().split('=');
        if (name === cookieName) {
            return decodeURIComponent(value);
        }
    }
    return null;
};

let isClarityEnabled = false;
let isClarityInitialized = false;

window.insertTextAtCursorPosition = (textAreaElement: HTMLTextAreaElement, text: string, dotNetRef: DotNet.DotNetObject): void => {
    if (!textAreaElement) return;

    const start = textAreaElement.selectionStart ?? 0;
    const end = textAreaElement.selectionEnd ?? 0;
    const value = textAreaElement.value ?? '';

    const newValue = value.substring(0, start) + text + value.substring(end);
    textAreaElement.value = newValue;
    textAreaElement.selectionStart = textAreaElement.selectionEnd = start + text.length;
    textAreaElement.focus();

    dotNetRef.invokeMethodAsync('OnTextAreaValueChanged', newValue);
//    dotNetRef.invokeMethodAsync('CloseEmojiPicker');
//    document.removeEventListener('click', window.closeEmojiPicker);
//    window.emojiPickerElementDotNetRef = undefined;
};

window.getSelectedEmoji = (event: any): string => {
    return event?.detail?.unicode || '';
};

window.initializeEmojiPicker = (emojiPickerElement: Element, textAreaElement: HTMLTextAreaElement, dotNetRef: DotNet.DotNetObject): void => {
    if (!emojiPickerElement || !textAreaElement) return;

    emojiPickerElement.addEventListener('emoji-click', (event: any) => {
        const emoji = window.getSelectedEmoji(event);
        if (emoji) {
            window.insertTextAtCursorPosition(textAreaElement, emoji, dotNetRef);
        }
    });

    window.emojiPickerElementDotNetRef = dotNetRef;
    document.addEventListener('click', window.closeEmojiPicker);
};

window.closeEmojiPicker = (event: MouseEvent): void => {
    const targetElement = event.target as HTMLElement;
    const isClickedWithinEmojiPicker = targetElement.closest('emoji-picker') !== null;
    if (isClickedWithinEmojiPicker) return;

    const dotNetRef = window.emojiPickerElementDotNetRef;
    if (!dotNetRef) {
        console.warn("No DotNet reference found for emoji picker. Cannot close emoji picker.");
        return;
    }

    dotNetRef.invokeMethodAsync('CloseEmojiPicker');
    document.removeEventListener('click', window.closeEmojiPicker);
    window.emojiPickerElementDotNetRef = undefined;
};

window.setClarityEnabled = (isEnabled: boolean): void => {
    isClarityEnabled = isEnabled;
};

window.startClarity = (): void => {
    if (!isClarityEnabled || isClarityInitialized) return;

    const cookie = getCookieValue('cconsent');
    const isConsent = cookie?.includes('"wanted":true') || false;

    if (!cookie || !isConsent) return;

    (function (c, l, a, r, i, t, y) {
        c[a] = c[a] || function () { (c[a].q = c[a].q || []).push(arguments) };
        t = l.createElement(r); t.async = 1; t.src = "https://www.clarity.ms/tag/" + i;
        y = l.getElementsByTagName(r)[0]; y.parentNode.insertBefore(t, y);
    })(window, document, "clarity", "script", "n8rso20w2o");

    isClarityInitialized = true;
    (window as any).clarity('consent', isConsent);
};

/**
 * Creates a portal tooltip using a Blazor-created tooltip element
 * JavaScript is only used for positioning, content is managed by Blazor
 */
window.createBlazorPortalTooltip = (triggerElement: HTMLElement, tooltipElement: HTMLElement, placement: string): TooltipInstance => {
    if (!(triggerElement instanceof HTMLElement) || !(tooltipElement instanceof HTMLElement)) {
        return {
            show: (): void => { },
            hide: (): void => { },
            destroy: (): void => { }
        };
    }

    // Move the Blazor-created tooltip element to document.body for portal behavior
    document.body.appendChild(tooltipElement);

    const popperInstance = createPopper(triggerElement, tooltipElement, {
        placement: placement as Placement,
        modifiers:
            [
                {
                    name: 'offset',
                    options: { offset: [0, 8], },
                },
                {
                    name: 'arrow',
                    options: { element: '.tooltip-arrow', },
                },
                {
                    name: 'preventOverflow',
                    options: { boundary: 'viewport', padding: 8, },
                },
                {
                    name: 'flip',
                    options: { fallbackPlacements: ['top', 'bottom', 'left', 'right'], padding: 8, },
                },
            ],
    });

    // Set initial placement attribute
    tooltipElement.setAttribute('data-popper-placement', placement);

    return {
        show: (): void => {
            tooltipElement.style.opacity = '1';
            tooltipElement.style.visibility = 'visible';
            popperInstance.update();
        },
        hide: (): void => {
            tooltipElement.style.opacity = '0';
            tooltipElement.style.visibility = 'hidden';
        },
        destroy: (): void => {
            popperInstance.destroy();
            // Move the element back to its original parent or remove it
            tooltipElement.parentNode?.removeChild(tooltipElement);
        }
    };
};

window.createPortalMenu = (triggerElement: HTMLElement, menuElement: HTMLElement, hostElement: HTMLElement, placement: string, dotNetRef: DotNet.DotNetObject): TooltipInstance => {
    if (!(triggerElement instanceof HTMLElement) || !(menuElement instanceof HTMLElement) || !(hostElement instanceof HTMLElement)) {
        return {
            show: (): void => { },
            hide: (): void => { },
            destroy: (): void => { }
        };
    }

    const restoreMenuToHost = (): void => {
        if (!hostElement.isConnected || menuElement.parentElement === hostElement) {
            return;
        }

        hostElement.appendChild(menuElement);
    };

    const moveMenuToBody = (): void => {
        if (menuElement.parentElement === document.body) {
            return;
        }

        document.body.appendChild(menuElement);
    };

    moveMenuToBody();

    const notifyPlacementChanged = (): void => {
        const resolvedPlacement = menuElement.getAttribute('data-popper-placement') || placement;
        const isDisplayedAboveTrigger = resolvedPlacement.startsWith('top');
        dotNetRef.invokeMethodAsync('SetMenuPlacement', isDisplayedAboveTrigger);
    };

    const popperInstance = createPopper(triggerElement, menuElement, {
        placement: placement as Placement,
        modifiers:
            [
                {
                    name: 'offset',
                    options: {
                        offset: ({ reference }) => [0, -reference.height],
                    },
                },
                {
                    name: 'preventOverflow',
                    options: { boundary: 'viewport', padding: 8, altAxis: true, },
                },
                {
                    name: 'flip',
                    options: { fallbackPlacements: ['bottom-end', 'top-start', 'top-end'], padding: 8, },
                },
                {
                    name: 'applyPlacementState',
                    enabled: true,
                    phase: 'write',
                    fn: ({ state }) => {
                        menuElement.setAttribute('data-popper-placement', state.placement);
                        notifyPlacementChanged();
                    },
                },
            ],
    });

    menuElement.setAttribute('data-popper-placement', placement);

    let isVisible = false;

    const removeGlobalHandlers = (): void => {
        document.removeEventListener('mousedown', handlePointerDown);
        document.removeEventListener('touchstart', handlePointerDown);
        document.removeEventListener('keydown', handleKeyDown);
    };

    const handlePointerDown = (event: MouseEvent | TouchEvent): void => {
        if (!isVisible) return;

        const target = event.target as Node | null;
        if (!target) return;
        if (triggerElement.contains(target) || menuElement.contains(target)) return;

        dotNetRef.invokeMethodAsync('CloseFromJavaScript');
    };

    const handleKeyDown = (event: KeyboardEvent): void => {
        if (!isVisible || event.key !== 'Escape') return;

        dotNetRef.invokeMethodAsync('CloseFromJavaScript');
    };

    return {
        show: (): void => {
            removeGlobalHandlers();
            isVisible = true;
            moveMenuToBody();
            menuElement.style.opacity = '1';
            menuElement.style.visibility = 'visible';
            menuElement.style.pointerEvents = 'auto';
            document.addEventListener('mousedown', handlePointerDown);
            document.addEventListener('touchstart', handlePointerDown);
            document.addEventListener('keydown', handleKeyDown);
            popperInstance.update();
            notifyPlacementChanged();
        },
        hide: (): void => {
            isVisible = false;
            removeGlobalHandlers();
            menuElement.style.opacity = '0';
            menuElement.style.visibility = 'hidden';
            menuElement.style.pointerEvents = 'none';
            restoreMenuToHost();
        },
        destroy: (): void => {
            isVisible = false;
            removeGlobalHandlers();
            popperInstance.destroy();
            restoreMenuToHost();
        }
    };
};

interface DragDropComponent {
    element: Element;
    componentRef: DotNet.DotNetObject;
}

const dragDropComponents: DragDropComponent[] = [];

window.registerDragDropComponent = (element: Element, componentRef: DotNet.DotNetObject) => {
    dragDropComponents.push({ element, componentRef });
};

window.unregisterDragDropComponent = (element: Element) => {
    const index = dragDropComponents.findIndex(c => c.element === element);
    if (index > -1) dragDropComponents.splice(index, 1);
};

let isDraggingOnPage = false;

document.addEventListener('dragenter', (e) => {
    if (dragDropComponents.length === 0) return;
    
    if (!isDraggingOnPage) {
        isDraggingOnPage = true;
        dragDropComponents.forEach(comp => {
            comp.componentRef.invokeMethodAsync('OnGlobalDragEnter');
        });
    }
});

document.addEventListener('dragleave', (e) => {
    if (e.clientX === 0 && e.clientY === 0) {
        isDraggingOnPage = false;
        dragDropComponents.forEach(comp => {
            comp.componentRef.invokeMethodAsync('OnGlobalDragLeave');
        });
    }
});

document.addEventListener('drop', (e) => {
    isDraggingOnPage = false;
    dragDropComponents.forEach(comp => {
        comp.componentRef.invokeMethodAsync('OnGlobalDragLeave');
    });
});

document.addEventListener('dragover', (e) => {
    // Prevent default to allow drop listener to fire
    // Only prevent default when we have registered components
    if (dragDropComponents.length > 0) {
        e.preventDefault();
    }
});
